import FormGroup from '../../../Form/FormGroup';
import {
  AnniProjectProps,
  AnniProjectDataType,
  AnniProjectActionType,
  AnniProjectMessage,
  isNew,
} from '../AnniProjectPopupInterface';
import { AttFileControllerService } from '@/__generated__/CommonApi';
import { AnniversaryProjectControllerService } from '@/__generated__/FrontApi';
import AttachmentPreview from '@/components/AttachmentImage.anni';
import Button from '@/components/Button';
import ErrorMessage from '@/components/Form/ErrorMessage';
import LabelText from '@/components/Form/LabelText';
import Col from '@/components/Grid/Col';
import Row from '@/components/Grid/Row';
import HR from '@/components/HR';
import InputDate from '@/components/Input/InputDate';
import InputNumber from '@/components/Input/InputNumber';
import InputRadio, { InputRadioGroup } from '@/components/Input/InputRadio';
import InputText from '@/components/Input/InputText';
import InputTextarea from '@/components/Input/InputTextarea';
import Select from '@/components/Select';
import { breakpoint, breakpointReverse } from '@/helpers/BreakpointHelper';
import {
  useSupportCategoriesForAnniPrjt,
  useAllSupportAnniType,
} from '@/helpers/SupportAnniPrjtHelper';
import { usePopupStore } from '@/stores/PopupStore';
import { useUserStore } from '@/stores/UserStore';
import React, { FC, useEffect, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

const FormSection = styled.form`
  margin: 32px 0 96px;
  max-width: 600px;
  display: inline-block;
  text-align: initial;

  hr {
    margin: 40px 0 30px 0;
  }

  .col-wrap {
    display: flex;
    align-items: center;
    margin: 0 -16px;
    flex-wrap: wrap;

    .col {
      width: 50%;
      padding: 0 16px;
    }
  }

  .form-item {
    padding: 13px 0;
  }

  ${FormGroup}.agree-wrap {
    margin-bottom: 0;
    .agree-txt {
      line-height: 42px;
    }
    .agree-rdo {
      margin: 42px 0 0 0;
      text-align: right;
    }
    ${breakpointReverse(`tablet`)} {
      .agree-rdo ${InputRadioGroup} label {
        display: inline-block;
        margin-right: 40px;
      }
      .agree-rdo ${InputRadioGroup} label:last-child {
        margin-right: 0;
      }
    }
  }

  & .btn-submit {
    margin-top: 80px;
  }

  ${FormGroup} {
    textarea {
      height: 210px;
    }
  }

  ${ErrorMessage} {
    margin-top: 5px;
  }

  .agree-dl {
    display: flex;
    align-items: center;
    margin-top: 25px;
    dd {
      margin-left: auto;
      label:first-of-type {
        margin-right: 30px;
      }
    }
    ${breakpoint(400)} {
      display: block;
      dt {
        margin-bottom: 5px;
      }
    }
  }

  ${breakpoint(`tablet`)} {
    .col-wrap {
      .col {
        width: 100%;
      }
    }

    ${FormGroup} {
      .col {
        width: 100%;
        margin-bottom: 24px;

        &:last-child {
          margin-bottom: 0;
        }
      }
      textarea {
        height: 66.6vw;
      }
    }
    ${FormGroup}.agree-wrap {
      .agree-txt {
        line-height: 30px;
      }
      .agree-rdo {
        margin: 15px 0 0 0;
      }
      .agree-rdo ${InputRadioGroup} label {
        text-align: left;
      }

      ${Row} {
        display: block;
      }
    }
    & .btn-submit {
      margin-top: 64px;
    }
  }
`;

const PopNoti = styled.ul`
  background-color: #f8f9fa;
  padding: 20px 15px 30px;
  margin: 49px 0 0 0;
  & li {
    margin-left: 15px;
    position: relative;
    font-size: 14px;
    line-height: 2;
    &::before {
      content:'·';
      display: block;
      position: absolute;
      top: 0;
      left: -10px;
      font-weight: 900;
    }
    &.nodisc {
      margin-left: 6px;
      &::before {
        display: none;
      }
    }
  }
  ${breakpoint(`tablet`)} {
    margin: 62px 0 0 0;
  }
  ${breakpoint(`mobile`)} {
    & li {
      line-height: 1.35;
      margin: 4px 0 2px 15px;
      letter-spacing: -0.84px;
      &.nodisc {
        line-height: 1.64;
        letter-spacing: -0.56px;
        strong {
          display: inline-block;
          margin: 25px 0 10px;
        }
      }
    }
  }
`;
const AnniProjectForm: FC<AnniProjectProps> = ({ isOpen, ...props }) => {
  const initFormValue: AnniProjectDataType = {
    confirmPushYn: 'Y',
    confirmPublicYn: 'Y',
  };
  const userStore = useUserStore();
  const popupStore = usePopupStore();
  const [images, setImages] = useState<File[]>([]);
  const [isUpdate, setIsUpdate] = useState(false);
  const [confirmPublic, setConfirmPublic] = useState(
    initFormValue.confirmPublicYn,
  );

  /* 기념기부 종류 */
  const anniTypes = useAllSupportAnniType();
  /* 일시 후원이고 기념기부 제외 출력 */
  const supportCategories = useSupportCategoriesForAnniPrjt();

  /* 폼 활성화 여부 */
  const disabled = !userStore.isLoggedIn();

  /* 팝업 오픈시 폼 초기화 */
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AnniProjectDataType>({
    // 가변 defaultValues
    defaultValues: initFormValue,
  });

  useEffect(() => {
    // 사용자 정보가 로딩되거나 Modal Close 시 form reset
    if (userStore.user) {
      // form reset
      reset();

      // images reset
      setImages([]);
    }
  }, [userStore.user, reset, isOpen]);

  const handelChange = (ev: any) => {
    setConfirmPublic(ev.target.value);
  };

  const onSubmit = useCallback(
    async (data: AnniProjectDataType) => {
      if (isUpdate === true) {
        popupStore.show('페이지가 등록중 입니다. 잠시만 기다려주세요');
        return;
      }
      setIsUpdate(true);

      // date validate
      if (data.goalEndDate && data.goalStartDate) {
        const eDate = new Date(data.goalEndDate || '');
        const sDate = new Date(data.goalStartDate || '');

        if (eDate.getTime() < sDate.getTime()) {
          popupStore.show('목표기간은 시작일이 종료일보다 나중일 수 없습니다.');
          setIsUpdate(false);
          return;
        }
      }

      const supportInforData: any = {
        solicitCode: '',
        supportCode: '',
      };
      // 지원사업 코드 셋팅
      if (data.supportIndexNumber) {
        supportCategories.forEach((item) => {
          if (item.indexNumber === Number(data.supportIndexNumber)) {
            supportInforData.solicitCode = item.solicitCode || '';
            supportInforData.supportCode = item.code || '';
          }
        });
      }

      // update files
      let aprjtAttImg: any = '';
      if (images.length > 0) {
        const result: any = await AttFileControllerService.uploadUsingPost({
          file: images,
        });
        if (result && result.resultCode === 'success' && result.data) {
          aprjtAttImg = result.data.attGrpNo;
        }
      }

      /* 230308 - 담당자 요청으로 동의항목 삭제
      if (data.confirmPublicYn !== 'Y') {
        popupStore.show('페이지 공개에 동의해야 페이지 생성이 가능합니다.');
        setIsUpdate(false);
        return;
      }
      */

      // 폼데이터로 변환
      const formData = new FormData();
      const finalData = {
        aprjtAttImg: aprjtAttImg || '',
        confirmPushYn: 'Y',
        confirmPublicYn: 'Y', // 230308 - 담당자 요청으로 동의항목 삭제
        ...supportInforData,
        ...data,
      };
      Object.keys(finalData).forEach((key) => {
        if (finalData[key] !== undefined) {
          formData.append(key, finalData[key]);
        }
      });

      // update form
      try {
        const submitRes: any = await AnniversaryProjectControllerService.insertAnniversaryProjectUsingPost(
          finalData,
        );

        setIsUpdate(false);

        if (submitRes?.resultCode === 'success') {
          // success
          popupStore.show('페이지가 등록되었습니다.');
          if (submitRes?.data?.idx === 0) {
            props.onRequestClose();
          } else {
            props.goto(AnniProjectActionType.complete, submitRes?.data?.idx);
          }
        } else {
          // error
          popupStore.show(
            '페이지 생성에 실패했습니다. 잠시후에 다시 시도해주세요.',
          );
        }
      } catch (ex) {
        popupStore.show(
          '페이지 생성에 실패했습니다. 잠시후에 다시 시도해주세요.',
        );

        setIsUpdate(false);
      }
    },
    [popupStore, images, supportCategories, isUpdate, props],
  );



  return (
    <FormSection onSubmit={handleSubmit(onSubmit)}>
      <FormGroup>
        <LabelText require>페이지명</LabelText>
        <InputText
          name="aprjtName"
          ref={register({
            required: {
              value: true,
              message: `필수 입력입니다`,
            },
          })}
          placeholder="페이지명 입력"
          maxLength={20}
          disabled={disabled}
          css="text-overflow: ellipsis;"
        />
        {errors.aprjtName && (
          <ErrorMessage>{errors.aprjtName.message}</ErrorMessage>
        )}
      </FormGroup>
      <FormGroup>
        <LabelText>사진</LabelText>
        <AttachmentPreview
          maxFileCount={1}
          files={images}
          onFilesChange={setImages}
          accept="image/*"
          disabled={disabled}
        />
      </FormGroup>
      <FormGroup>
        <LabelText require>메시지</LabelText>
        <InputTextarea
          name="aprjtMessage"
          ref={register({
            required: {
              value: true,
              message: `필수 입력입니다`,
            },
            maxLength: {
              value: 1000,
              message: '메시지는 최대 1000자까지 입력 가능합니다.',
            },
          })}
          placeholder="메시지를 입력해주세요(1000자 이내)"
          maxLength={1000}
          disabled={disabled}
        />
        {errors.aprjtMessage && (
          <ErrorMessage>{errors.aprjtMessage.message}</ErrorMessage>
        )}
      </FormGroup>
      <FormGroup>
        <LabelText require>기념일 종류</LabelText>
        <Select
          name="anniType"
          ref={register({
            required: {
              value: true,
              message: `필수 입력입니다`,
            },
          })}
          disabled={disabled}
        >
          <option value="">기념일 종류 선택</option>
          {anniTypes.map((a) => (
            <option key={a.code} value={a.code} data-name={a.codeName}>
              {a.codeName}
            </option>
          ))}
        </Select>
        {errors.anniType && (
          <ErrorMessage>{errors.anniType.message}</ErrorMessage>
        )}
      </FormGroup>
      <FormGroup>
        <LabelText require>기념일</LabelText>
        <InputDate
          name="anniDate"
          css="width:100%;"
          ref={register({
            required: {
              value: true,
              message: `필수 입력입니다`,
            },
          })}
          disabled={disabled}
          initialDate={initFormValue.anniDate}
        />
        {errors.anniDate && (
          <ErrorMessage>{errors.anniDate.message}</ErrorMessage>
        )}
      </FormGroup>
      {/*
        // 221216 고객사 요청으로 항목 삭제
        <FormGroup>
          <LabelText require>지원사업</LabelText>
          <Select
            name="supportIndexNumber"
            ref={register({
              required: {
                value: true,
                message: `필수 입력입니다`,
              },
            })}
            disabled={disabled}
            readOnly={disabled}
          >
            <option value="">후원 분야 선택</option>
            {supportCategories.map((category) => (
              <option key={category.indexNumber} value={category.indexNumber}>
                {category.codeName}
              </option>
            ))}
          </Select>
          {errors.anniType && (
            <ErrorMessage>{errors.anniType.message}</ErrorMessage>
          )}
        </FormGroup>
         */}
      <FormGroup>
        <LabelText require>목표금액</LabelText>
        <InputNumber
          name="goalAmount"
          placeholder="목표금액 입력(숫자만입력)"
          ref={register({
            required: {
              value: true,
              message: `필수 입력입니다`,
            },
            min: { value: 1, message: '목표금액은 0원 이상 입력해주세요.' },
          })}
          disabled={disabled}
          defaultValue={initFormValue.goalAmount}
        />
        {errors.goalAmount && (
          <ErrorMessage>{errors.goalAmount.message}</ErrorMessage>
        )}
      </FormGroup>
      <FormGroup>
        <LabelText require>목표기간</LabelText>
        <Row>
          <Col desktop={6} mobile={3}>
            <LabelText require>시작</LabelText>
            <InputDate
              name="goalStartDate"
              css="width:100%;"
              ref={register({
                required: {
                  value: true,
                  message: `필수 입력입니다`,
                },
              })}
              disabled={disabled}
              initialDate={initFormValue.goalStartDate}
            />
          </Col>
          <Col align="center" desktop={1} mobile={1}>
            ~
          </Col>
          <Col desktop={6} mobile={3}>
            <LabelText require>종료</LabelText>
            <InputDate
              name="goalEndDate"
              css="width:100%;"
              ref={register({
                required: {
                  value: true,
                  message: `필수 입력입니다`,
                },
              })}
              disabled={disabled}
              initialDate={initFormValue.goalEndDate}
            />
          </Col>
        </Row>
        {errors.goalStartDate
          ? errors.goalStartDate && (
              <ErrorMessage>{errors.goalStartDate.message}</ErrorMessage>
            )
          : errors.goalEndDate && (
              <ErrorMessage>{errors.goalEndDate.message}</ErrorMessage>
            )}
      </FormGroup>
      <HR />
      <FormGroup className="agree-wrap">
        <input type="hidden" name="confirmPublicYn" value="Y" />
        {/* 230308 - 담당자 요청으로 동의항목 삭제
        <Row>
          <Col desktop="6" className="agree-txt">
            ‘{AnniProjectMessage.DefaultTitle}’ 페이지 생성 시 회원정보에 등록된
            메일 주소로 안내 메일이 자동발송 됩니다.
            <br />
            생성하신 페이지는 유니세프 홈페이지 내에 게시되므로, 개인정보 등
            프라이버시 노출에 유의하여 주시기 바랍니다
          </Col>
          <Col desktop="4" className="agree-rdo">
            <InputRadioGroup full>
              {AnniProjectFormTerms.map((term) => (
                <InputRadio
                  key={term.value}
                  name="confirmPublicYn"
                  label={term.text}
                  value={term.value}
                  tab={false}
                  ref={register}
                  disabled={disabled}
                  checked={term.value === confirmPublic}
                  onChange={handelChange}
                />
              ))}
            </InputRadioGroup>
            {errors.confirmPublicYn && (
              <ErrorMessage>{errors.confirmPublicYn.message}</ErrorMessage>
            )}
          </Col>
        </Row>
         */}

        <div className="dec agree">
          ‘같이 하는 기념기부’ 페이지가 공개 및 검색될 수 있습니다.
        </div>
        <dl className="agree-dl">
          <dt>페이지 생성에 동의하십니까?</dt>
          <dd>
            <InputRadio
              label="동의함"
              name="isAgreeYn"
              value="Y"
              ref={register({
                required: {
                  value: true,
                  message: `페이지 생성에 동의해주세요.`,
                },
              })}
            />
            <InputRadio label="동의하지 않음" name="isAgreeYn" value="N" />
          </dd>
        </dl>
        {errors.isAgreeYn && (
          <ErrorMessage>{errors.isAgreeYn.message}</ErrorMessage>
        )}
        <PopNoti>
          <li>
            ‘{AnniProjectMessage.DefaultTitle}’ 페이지 생성 시 회원정보에 등록된
            메일 <span>주소로 안내 메일이 자동발송 됩니다.</span>
          </li>
          <li>
            생성한 페이지는 유니세프 홈페이지에 게시되므로, 사생활 등 민감정보 노출에 유의하여 주시기 바랍니다.
          </li>
          <li>
          생성한 페이지의 정보 수정은 관리자의 승인 후 페이지 생성이 완료되었을 시 가능합니다.
          </li>
          {isNew?(
          <li className='nodisc'>
            <strong>[이벤트 기간: 2023. 08. 10 ~ 이벤트 종료시까지]</strong><br/>
            ‘{AnniProjectMessage.DefaultTitle}’ 이벤트에 참여하여 주시면, <br/>
            추첨을 통해 등록되어 있는 휴대폰 번호로 연락 및 경품을 전달 드립니다.<br/> 
            (휴대폰 번호 정보는 마이페이지에서 등록 및 수정이 가능합니다.)
          </li>
          ):(
            <></>
          )}
        </PopNoti>
      </FormGroup>
      <Row>
        <Button type="submit" full className="btn-submit" disabled={disabled}>
          페이지 생성
        </Button>
      </Row>
    </FormSection>
  );
};

export default AnniProjectForm;
